import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { SiteNav } from "@/components/SiteNav";
import { api } from "@/lib/api";

const Card = ({ children }) => (
  <div className="min-h-screen"><SiteNav />
    <div className="max-w-lg mx-auto px-6 py-24 text-center">{children}</div>
  </div>
);

export function PaymentCancel() {
  const [params] = useSearchParams();
  const mailId = params.get("mail_id");
  return (
    <Card>
      <XCircle size={44} className="mx-auto text-amber-500" />
      <h1 className="font-display text-3xl font-bold mt-5" data-testid="payment-cancel-title">Payment cancelled</h1>
      <p className="text-slate-500 mt-3">Your letter is saved and awaiting payment. It won't be sent until payment completes.</p>
      <Link to={mailId ? `/app/mail/${mailId}` : "/app"} data-testid="payment-cancel-back" className="inline-flex mt-8 px-6 py-3 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 font-semibold">Back to letter</Link>
    </Card>
  );
}

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking" });

  useEffect(() => {
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setState({ status: "paid", mailId: data.mail_id, batchId: data.batch_id });
        if (data.status === "expired") return setState({ status: "expired" });
      } catch {
        /* keep polling */
      }
      if (++tries < 15) timer = setTimeout(poll, 2000);
      else setState({ status: "timeout" });
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  if (state.status === "paid") {
    return (
      <Card>
        <CheckCircle2 size={48} className="mx-auto text-emerald-600" />
        <h1 className="font-display text-3xl font-bold mt-5" data-testid="payment-success-title">{state.batchId ? "Batch sending" : "Letter sent"}</h1>
        <p className="text-slate-500 mt-3">{state.batchId ? "Payment confirmed. Your letters are being released and each recipient is notified by email and SMS, no attachments." : "Payment confirmed. The recipient has been notified by email and SMS — no attachment. Track every step from your dashboard."}</p>
        <Link to={state.batchId ? `/app/bulk/${state.batchId}` : `/app/mail/${state.mailId}`} data-testid="payment-success-track-btn" className="inline-flex mt-8 px-6 py-3 rounded-full bg-emerald-600 text-white font-semibold hover:bg-emerald-500 transition-colors">{state.batchId ? "Track batch" : "Track letter"}</Link>
      </Card>
    );
  }
  return (
    <Card>
      {state.status === "checking" ? <Loader2 size={40} className="mx-auto animate-spin text-emerald-600" /> : <XCircle size={44} className="mx-auto text-amber-500" />}
      <h1 className="font-display text-2xl font-bold mt-5" data-testid="payment-status-title">
        {state.status === "checking" ? "Confirming payment…" : state.status === "expired" ? "Checkout expired" : "Still confirming"}
      </h1>
      {state.status !== "checking" && <Link to="/app" className="inline-flex mt-8 underline">Go to dashboard</Link>}
    </Card>
  );
}
